import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { can, ROLE_LABELS, type Permission, type Role } from '@aq/shared';
import { useAuth } from '../features/auth/AuthContext.tsx';
import { GlobalSearch } from '../features/search/GlobalSearch.tsx';
import { TimeZonePrompt } from '../features/profile/TimeZonePrompt.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { BRAND, BrandIcon, Icons } from '../components/icons.tsx';
import { Tooltip } from '../components/Tooltip.tsx';
import { StorageMeter } from '../features/storage/StorageMeter.tsx';
import { StarGlyph } from '../features/drive/StarButton.tsx';
import { cx } from '../components/ui.tsx';
import { logoUrl, useBranding } from '../lib/profile-queries.ts';
import { formatTime, formatWeekdayDate } from '../lib/display.ts';
import { UploadBar } from '../features/drive/UploadBar.tsx';
import { useIsSmallScreen } from '../lib/useMediaQuery.ts';

/**
 * The frame every screen sits in.
 *
 * Grouped navigation rather than one long list: the client's people think in
 * terms of "the documents" and "the books" and "who can get in", and a flat
 * list of nine items makes them read all nine every time. The headings do the
 * narrowing before the eye reaches the labels.
 *
 * The header holds only search and the clock. Everything about *you* — your
 * picture, your name, signing out — is at the foot of the sidebar, which is
 * where people now expect to find it and where it stays out of the way of the
 * work.
 */

interface NavItem {
  to: string;
  label: string;
  /** Path under /brand. The sidebar is one of the two places using the
   *  client's own artwork rather than the drawn monochrome set. */
  icon?: string;
  /**
   * Drawn instead, for an entry the client's icon set has no picture for.
   *
   * A star has to be a star — recognisable at 20px and filled in the same
   * amber it is everywhere else — and inventing a PNG for it would mean two
   * stars in the product that could drift apart.
   */
  glyph?: ReactNode;
  hint: string;
  permission?: Permission;
}

const NAV_GROUPS: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Workspace',
    items: [
      {
        to: '/dashboard',
        label: 'Dashboard',
        icon: BRAND.dashboard,
        hint: 'Totals, storage, and the things you opened last',
        permission: 'dashboard:read',
      },
      {
        to: '/drive',
        label: 'Drive',
        icon: BRAND.drive,
        hint: 'Every folder, sheet and uploaded file',
      },
      {
        to: '/starred',
        label: 'Starred',
        glyph: <StarGlyph filled />,
        hint: 'The things you are working on, whatever folder they are in',
      },
      {
        to: '/trash',
        label: 'Trash',
        icon: BRAND.trash,
        hint: 'Deleted items, and how to put them back',
        // Nothing to put back if you cannot put anything in.
        permission: 'file:write',
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      {
        to: '/users',
        label: 'Users',
        icon: BRAND.users,
        hint: 'Accounts and what each may do',
        permission: 'user:read',
      },
      {
        to: '/audit',
        label: 'Audit log',
        icon: BRAND.audit,
        hint: 'Everything that has happened, in order',
        permission: 'audit:read',
      },
      {
        to: '/sessions',
        label: 'Sign-ins',
        icon: BRAND.sessions,
        hint: 'Where your account is currently signed in',
        permission: 'session:read',
      },
      {
        to: '/security',
        label: 'Security',
        icon: BRAND.security,
        hint: 'Password and two-factor authentication',
      },
    ],
  },
];

const COLLAPSE_KEY = 'aq.sidebar.collapsed';

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();

  // Remembered, because a person who collapses the sidebar means it.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true');
  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  /**
   * On a phone the sidebar is a drawer, not a column.
   *
   * 248px of permanent navigation on a 390px screen leaves too little for the
   * thing you came to look at, so below `md` it slides in over the content and
   * is closed the rest of the time. Deliberately not remembered: a drawer that
   * reopens itself on every visit is a drawer in the way.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Following a link is the end of what the drawer was opened to do.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  /**
   * The collapse preference is a desktop idea.
   *
   * Below md the sidebar is a drawer that is either open or absent, so a 68px
   * icon rail there would be the worst of both: too wide to ignore, too narrow
   * to read. Someone who collapsed it on their laptop still gets labels on
   * their phone.
   */
  /**
   * The editors run without the shell's own header.
   *
   * A spreadsheet is a full-bleed workspace: Excel has nothing above the
   * ribbon but the title bar, and every row of chrome we add is a row of the
   * ledger the client cannot see. On a 768px laptop the search-and-clock bar
   * was 72px, and the page underneath already has a header of its own with
   * the name, the save state and the way back.
   */
  const inEditor =
    location.pathname.startsWith('/sheets/') || location.pathname.startsWith('/documents/');

  const small = useIsSmallScreen();
  const railed = collapsed && !small;

  const logo = logoUrl(branding.data);

  return (
    <div
      className="flex h-screen bg-canvas"
      /*
       * Room for the upload strip, while there is one.
       *
       * It is fixed across the top of the window, over everything — which is
       * right for something that has to stay visible wherever the user goes,
       * and wrong when it lands on top of the only way to reach something.
       * In an editor there is no page header beneath it: the workbook's own
       * band of controls — its name, Export, Full screen — is a few pixels
       * at the very top that open when pointed at, and they were under the
       * strip and unreachable for as long as an upload ran.
       *
       * So the page gives the strip its height rather than sharing it. The
       * variable is set by the strip itself and is zero the rest of the time,
       * which is every screen and every moment when nothing is uploading.
       */
      style={{ paddingTop: 'var(--aq-upload-bar, 0px)' }}
    >
      {/* Fixed across the very top, over everything, so an upload started in
          the Drive stays visible wherever the user goes next. */}
      <UploadBar />

      {/* Catches the tap that means "put that away". Only ever present on the
          small layout, where the drawer floats over the page. */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      ) : null}

      <nav
        aria-label="Main"
        className={cx(
          'flex shrink-0 flex-col border-r border-border-strong bg-surface transition-[width] duration-normal',
          // Small screens: an overlay drawer, full width regardless of the
          // desktop collapse preference — a 68px rail is a phone's worst of
          // both, too wide to ignore and too narrow to read.
          'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-[264px]',
          'max-md:transition-transform max-md:duration-normal',
          drawerOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
          railed ? 'w-[68px]' : 'w-[248px]',
        )}
      >
        <div
          className={cx(
            'flex h-[72px] shrink-0 items-center gap-2.5 border-b border-border px-3',
            railed && 'justify-center px-0',
          )}
        >
          <img
            src={logo ?? '/brand/logo.png'}
            alt="Abdul Qadir Finance System"
            className={cx('w-auto rounded-md object-contain', railed ? 'h-9' : 'h-12')}
          />
          {!railed ? (
            <Tooltip label="Collapse the sidebar" shortcut="[">
              <button
                type="button"
                aria-label="Collapse the sidebar"
                onClick={() => setCollapsed(true)}
                className="ml-auto rounded-md p-1.5 text-muted transition-fast hover:bg-sunken hover:text-ink"
              >
                <PanelGlyph />
              </button>
            </Tooltip>
          ) : null}
        </div>

        {railed ? (
          <div className="flex justify-center py-2">
            <Tooltip label="Expand the sidebar" shortcut="[" side="right">
              <button
                type="button"
                aria-label="Expand the sidebar"
                onClick={() => setCollapsed(false)}
                className="rounded-md p-1.5 text-muted transition-fast hover:bg-sunken hover:text-ink"
              >
                <PanelGlyph flipped />
              </button>
            </Tooltip>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter(
              (item) => !item.permission || (user && can(user.role, item.permission)),
            );
            if (items.length === 0) return null;

            return (
              <div key={group.heading} className="mb-4 last:mb-0">
                {railed ? (
                  <div className="mx-3 mb-2 h-px bg-[var(--border)]" aria-hidden />
                ) : (
                  <h2 className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-subtle">
                    {group.heading}
                  </h2>
                )}

                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <Tooltip
                        label={item.label}
                        detail={item.hint}
                        side={railed ? 'right' : 'bottom'}
                      >
                        <NavLink
                          to={item.to}
                          className={({ isActive }) =>
                            cx(
                              'relative flex items-center gap-3 rounded-lg py-2 text-body-sm transition-fast',
                              railed ? 'justify-center px-0' : 'px-3',
                              isActive
                                ? 'bg-blue-50 font-semibold text-blue-700'
                                : 'text-body hover:bg-sunken hover:text-ink',
                            )
                          }
                        >
                          {({ isActive }) => (
                            <>
                              {/* The active marker sits in the gutter so it
                                  reads the same railed or not. */}
                              {isActive ? (
                                <span
                                  className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--blue-600)]"
                                  aria-hidden
                                />
                              ) : null}
                              {item.glyph ?? <BrandIcon src={item.icon ?? ''} size={20} />}
                              {railed ? null : item.label}
                            </>
                          )}
                        </NavLink>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Above the account, as Drive has it: the gauge nobody thinks to go
            looking for until an upload has already failed. Only for those who
            may see the system's own figures — the same right as the
            dashboard. */}
        {user && can(user.role, 'dashboard:read') ? (
          <div className="shrink-0 border-t border-border p-2">
            <StorageMeter railed={railed} />
          </div>
        ) : null}

        <div className="shrink-0 border-t border-border p-2">
          <Tooltip
            label={user?.name ?? 'Profile'}
            detail="Your details, picture and preferences"
            side={railed ? 'right' : 'bottom'}
          >
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className={cx(
                'flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-fast hover:bg-sunken',
                railed && 'justify-center',
              )}
            >
              <Avatar
                name={user?.name ?? '?'}
                userId={user?.id}
                hasAvatar={user?.hasAvatar ?? false}
                version={user?.updatedAt}
                size={railed ? 'sm' : 'md'}
              />
              {railed ? null : (
                <span className="min-w-0 flex-1">
                  {/* Two lines before truncating. A full name does not fit
                      beside the avatar on one — "Muhammad Shahood Rehan"
                      came out as "Muhammad Shahood …" — and the footer has
                      the room. Anything longer still truncates rather than
                      pushing the sign-out button off the bottom. */}
                  <span className="line-clamp-2 text-body-sm font-medium leading-snug text-ink">
                    {user?.name}
                  </span>
                  <span className="block truncate text-micro text-muted">
                    {user ? ROLE_LABELS[user.role as Role] : ''}
                  </span>
                </span>
              )}
            </button>
          </Tooltip>

          <Tooltip
            label="Sign out"
            detail="Ends this session on this device"
            side={railed ? 'right' : 'bottom'}
          >
            <button
              type="button"
              onClick={() => void logout()}
              className={cx(
                'mt-0.5 flex w-full items-center gap-3 rounded-lg py-2 text-body-sm text-muted transition-fast hover:bg-danger-bg hover:text-[var(--danger)]',
                railed ? 'justify-center px-0' : 'px-3',
              )}
            >
              <BrandIcon src={BRAND.signOut} size={20} />
              {railed ? null : 'Sign out'}
            </button>
          </Tooltip>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        {inEditor ? null : (
          <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-border-strong bg-surface px-3 md:h-[72px] md:gap-6 md:px-6">
            <button
              type="button"
              aria-label="Open the menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              className="-ml-1 shrink-0 rounded-md p-2 text-muted transition-fast hover:bg-sunken hover:text-ink md:hidden"
            >
              <MenuGlyph />
            </button>

            <GlobalSearch />

            {/* The clock is the first thing to go when width is scarce: it is
              ambient, and the phone already has one in its status bar. */}
            <div className="ml-auto hidden sm:block">
              <Clock />
            </div>
          </header>
        )}

        {/*
          Scrolls on small screens, clips on large ones.

          `overflow-hidden` is deliberate at desktop width — the sheet grid and
          the document editor manage their own scrolling inside it, and a second
          scrollbar around them is a bug. On a phone the same rule made every
          page that overflowed simply unreachable: the Drive listing ran nearly
          200px past the right edge with no way to get there.
        */}
        <main
          className={cx(
            'min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden md:overflow-hidden',
            inEditor ? 'p-0' : 'px-3 py-3 md:px-6 md:py-4',
          )}
        >
          <Outlet />
        </main>
      </div>

      <TimeZonePrompt />
    </div>
  );
}

/**
 * The wall clock.
 *
 * Ticks every second rather than every minute: a clock that sits on :34 for
 * half a minute looks broken, and the cost of a state update per second on a
 * single element is nothing.
 */
function Clock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-sunken px-3.5 py-2">
      <span className="text-muted" aria-hidden>
        <Icons.Clock size={16} />
      </span>
      <div className="leading-tight">
        <div className="font-mono text-body-sm font-medium tabular-nums text-ink">
          {formatTime(now)}
        </div>
        <div className="text-micro text-muted">{formatWeekdayDate(now)}</div>
      </div>
    </div>
  );
}

function PanelGlyph({ flipped = false }: { flipped?: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={flipped ? { transform: 'scaleX(-1)' } : undefined}
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="m16 10-2 2 2 2" />
    </svg>
  );
}

/** The three-line menu button, shown only where the sidebar is a drawer. */
function MenuGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
